import React from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { selectProvinces, selectWardsByProvince } from "@/modules/addresses/redux/AddressRedux";
import { AddressFieldValue } from "@/modules/addresses/hooks/useAddressField";

// Import existing components
import DeliveryInformationSection from "./DeliveryInformationSection";
import TopOrder from "./TopOrder";
import OrderItemsTable from "./OrderItemsTable";
import OrderSummary from "./OrderSummary";
import OrderStaff from "./OrderStaff";

const INITIAL_LOAD_TIMEOUT = 2000;

interface OrderDetailInfoProps {
  formData: any;
  customers: any[];
  companies: any[];
  salesChannels: any[];
  wallets?: any[];
  orderStatuses: any[];
  employees: any[];
  orderItems: any[];
  units: any[];
  productTypes?: any[];
  additionalFees: any[];
  totalAmount: number;
  grandTotal: number;
  isEditMode: boolean;
  orderId?: string;
  contextCompanyId?: string;
  disabled?: boolean;
  onInputChange: (field: string, value: any) => void;
  onItemChange: (index: number, field: string, value: any) => void;
  onAddOrderItem: () => void;
  onAddGroupItem: () => void;
  onRemoveOrderItem: (index: number) => void;
  onReorderItems: (items: any[]) => void;
  onOrderStatusCreated: (status: any) => void;
  onOrderStatusUpdated?: () => Promise<void>;
  onSalesChannelCreated: (channel: any) => void;
  onWalletCreated?: (wallet: any) => void;
  onAdditionalFeesChange: (fees: any[]) => void;
  onPaymentCollected?: (transaction: any) => void;
}

const OrderDetailInfo: React.FC<OrderDetailInfoProps> = ({
  formData,
  customers,
  companies,
  salesChannels,
  wallets = [],
  orderStatuses,
  employees,
  orderItems,
  units,
  productTypes = [],
  additionalFees,
  totalAmount,
  grandTotal,
  isEditMode,
  orderId,
  contextCompanyId,
  disabled = false,
  onInputChange,
  onItemChange,
  onAddOrderItem,
  onAddGroupItem,
  onRemoveOrderItem,
  onReorderItems,
  onOrderStatusCreated,
  onOrderStatusUpdated,
  onSalesChannelCreated,
  onWalletCreated,
  onAdditionalFeesChange,
  onPaymentCollected,
}) => {
  const provinces = useSelector(selectProvinces);
  const addressManuallyEdited = React.useRef(false);
  const lastCustomerId = React.useRef<string>("");
  const autoFillExecuted = React.useRef(false);
  const isInitialLoad = React.useRef(true);
  const isAutoFilling = React.useRef(false);
  const shouldForceCustomerAddressUpdate = React.useRef(false);

  const resolveProvinceId = React.useCallback((provinceValue: any): string => {
    if (!provinceValue) return "";

    const asString = typeof provinceValue === "string" ? provinceValue : "";
    if (asString) {
      const exactById = provinces.find((province: any) => province.id === asString);
      if (exactById) return exactById.id;

      const exactByCode = provinces.find((province: any) => province.province_code === asString);
      if (exactByCode) return exactByCode.id;

      const exactByName = provinces.find((province: any) => province.name === asString);
      if (exactByName) return exactByName.id;
    }

    if (typeof provinceValue === "object") {
      if (typeof provinceValue.id === "string") return provinceValue.id;
      if (typeof provinceValue.province_code === "string") {
        const byCode = provinces.find((province: any) => province.province_code === provinceValue.province_code);
        if (byCode) return byCode.id;
      }
      if (typeof provinceValue.name === "string") {
        const byName = provinces.find((province: any) => province.name === provinceValue.name);
        if (byName) return byName.id;
      }
    }

    return asString || "";
  }, [provinces]);

  const currentProvinceId = formData.delivery_address_obj && typeof formData.delivery_address_obj === "object"
    ? resolveProvinceId(formData.delivery_address_obj.province)
    : "";

  const wardsForCurrentProvince = useSelector((state: any) =>
    selectWardsByProvince(state, currentProvinceId)
  );

  const resolveWardId = React.useCallback((wardValue: any): string => {
    if (!wardValue) return "";

    const asString = typeof wardValue === "string" ? wardValue : "";
    if (asString) {
      const exactById = wardsForCurrentProvince.find((ward: any) => ward.id === asString);
      if (exactById) return exactById.id;

      const exactByCode = wardsForCurrentProvince.find((ward: any) => ward.ward_code === asString);
      if (exactByCode) return exactByCode.id;

      const exactByName = wardsForCurrentProvince.find((ward: any) => ward.name === asString);
      if (exactByName) return exactByName.id;
    }

    if (typeof wardValue === "object") {
      if (typeof wardValue.id === "string") return wardValue.id;
      if (typeof wardValue.ward_code === "string") {
        const byCode = wardsForCurrentProvince.find((ward: any) => ward.ward_code === wardValue.ward_code);
        if (byCode) return byCode.id;
      }
      if (typeof wardValue.name === "string") {
        const byName = wardsForCurrentProvince.find((ward: any) => ward.name === wardValue.name);
        if (byName) return byName.id;
      }
    }

    return asString || "";
  }, [wardsForCurrentProvince]);

  const wrappedDeliveryInputChange = React.useCallback((field: string, value: any) => {
    if (field === "delivery_address_obj" && !isAutoFilling.current && !isInitialLoad.current) {
      addressManuallyEdited.current = true;
    }
    onInputChange(field, value);
  }, [onInputChange]);

  React.useEffect(() => {
    if (formData.customer_id && lastCustomerId.current !== formData.customer_id) {
      autoFillExecuted.current = false;
      addressManuallyEdited.current = false;
      // Do not force overwrite address on first edit-data hydration.
      // Force update only after initial load completes (real user customer change).
      shouldForceCustomerAddressUpdate.current = !(isEditMode && isInitialLoad.current && !lastCustomerId.current);
    }

    if (isInitialLoad.current) {
      setTimeout(() => {
        isInitialLoad.current = false;
      }, INITIAL_LOAD_TIMEOUT);
    }
  }, [formData.customer_id, isEditMode]);

  const initialAddress = React.useMemo<AddressFieldValue | null>(() => {
    if (typeof formData.delivery_address_obj === "string") {
      return null;
    }

    if (formData.delivery_address_obj && typeof formData.delivery_address_obj === "object") {
      return {
        address_line: formData.delivery_address_obj.address_line || "",
        province: resolveProvinceId(formData.delivery_address_obj.province) || formData.delivery_address_obj.province || "",
        ward: resolveWardId(formData.delivery_address_obj.ward) || formData.delivery_address_obj.ward || "",
        free_address: formData.delivery_address_obj.free_address || "",
      };
    }

    return null;
  }, [formData.delivery_address_obj, resolveProvinceId, resolveWardId]);

  React.useEffect(() => {
    const handleCustomerAddress = async () => {
      if (!formData.customer_id) return;

      const isCustomerChanged = lastCustomerId.current !== formData.customer_id;
      const shouldForceUpdate = shouldForceCustomerAddressUpdate.current || isCustomerChanged;

      if (autoFillExecuted.current && !shouldForceUpdate && lastCustomerId.current === formData.customer_id) {
        return;
      }

      if (isEditMode && formData.delivery_address_obj && !shouldForceUpdate) {
        if (typeof formData.delivery_address_obj === "string" && formData.delivery_address_obj.length > 0) {
          autoFillExecuted.current = true;
          lastCustomerId.current = formData.customer_id;
          return;
        }

        if (typeof formData.delivery_address_obj === "object" && "address_line" in formData.delivery_address_obj) {
          const hasData = !!(formData.delivery_address_obj.address_line && formData.delivery_address_obj.address_line.trim()) ||
            !!(formData.delivery_address_obj.province && formData.delivery_address_obj.province.trim()) ||
            !!(formData.delivery_address_obj.ward && formData.delivery_address_obj.ward.trim());

          if (hasData) {
            autoFillExecuted.current = true;
            lastCustomerId.current = formData.customer_id;
            return;
          }
        }
      }

      if (addressManuallyEdited.current && !shouldForceUpdate && lastCustomerId.current === formData.customer_id) {
        return;
      }

      const selectedCustomer = customers.find((customer: any) => customer.id === formData.customer_id);
      const addressObj = selectedCustomer?.address_obj_response || selectedCustomer?.address_obj || selectedCustomer?.address;

      if (selectedCustomer && addressObj) {
        const customerAddress: AddressFieldValue = {
          address_line: "",
          province: "",
          ward: "",
          free_address: "",
        };

        if (typeof addressObj === "object") {
          customerAddress.address_line = addressObj.address_line || "";
          customerAddress.free_address = addressObj.free_address || "";

          if (addressObj.province) {
            const provinceValue: any = addressObj.province;
            if (typeof provinceValue === "string") {
              customerAddress.province = resolveProvinceId(provinceValue) || provinceValue;
            } else if (provinceValue?.id) {
              customerAddress.province = resolveProvinceId(provinceValue.id) || provinceValue.id;
            } else if (provinceValue?.province_code) {
              customerAddress.province = resolveProvinceId(provinceValue.province_code) || provinceValue.province_code;
            } else if (provinceValue?.name) {
              customerAddress.province = resolveProvinceId(provinceValue.name) || provinceValue.name;
            }
          }

          if (addressObj.ward) {
            const wardValue: any = addressObj.ward;
            if (typeof wardValue === "string") {
              customerAddress.ward = wardValue;
            } else if (wardValue?.id) {
              customerAddress.ward = wardValue.id;
            } else if (wardValue?.ward_code) {
              customerAddress.ward = wardValue.ward_code;
            } else if (wardValue?.name) {
              customerAddress.ward = wardValue.name;
            }
          }
        }

        if (!customerAddress.province && !customerAddress.ward && customerAddress.address_line) {
          const addressText = customerAddress.address_line.toLowerCase();
          let foundProvince: any = null;
          let foundWard: any = null;

          for (const province of provinces) {
            const provinceName = province.name.toLowerCase();
            if (addressText.includes(provinceName)) {
              foundProvince = province;
              customerAddress.province = province.id;
              break;
            }
          }

          if (customerAddress.province) {
            for (const ward of wardsForCurrentProvince) {
              const wardName = ward.name.toLowerCase();
              if (addressText.includes(wardName)) {
                foundWard = ward;
                customerAddress.ward = ward.id;
                break;
              }
            }
          }

          const originalAddress = customerAddress.address_line;
          let streetAddress = customerAddress.address_line;

          if (foundProvince) {
            streetAddress = streetAddress.replace(new RegExp(foundProvince.name, "gi"), "").trim();
          }

          if (foundWard) {
            streetAddress = streetAddress.replace(new RegExp(foundWard.name, "gi"), "").trim();
          }

          streetAddress = streetAddress.replace(/,\s*,/g, ",").replace(/^,\s*|,\s*$/g, "").trim();
          customerAddress.address_line = streetAddress;
          customerAddress.free_address = originalAddress;
        }

        const currentAddress = formData.delivery_address_obj;
        const isCurrentAddressEmpty = !currentAddress ||
          (typeof currentAddress === "object" &&
            (!currentAddress.address_line && !currentAddress.province && !currentAddress.ward && !currentAddress.free_address)) ||
          (typeof currentAddress === "string" && currentAddress.length === 0);

        const needsUpdate = shouldForceUpdate ||
          isCurrentAddressEmpty ||
          isCustomerChanged ||
          (typeof currentAddress === "object" &&
            (currentAddress.address_line !== customerAddress.address_line ||
              currentAddress.province !== customerAddress.province ||
              currentAddress.ward !== customerAddress.ward ||
              currentAddress.free_address !== customerAddress.free_address));

        if (addressManuallyEdited.current && !shouldForceUpdate && lastCustomerId.current === formData.customer_id) {
          return;
        }

        if (needsUpdate) {
          isAutoFilling.current = true;
          wrappedDeliveryInputChange("delivery_address_obj", customerAddress);
          isAutoFilling.current = false;
          autoFillExecuted.current = true;
          lastCustomerId.current = formData.customer_id;
          shouldForceCustomerAddressUpdate.current = false;
        }
      } else {
        if (shouldForceUpdate) {
          isAutoFilling.current = true;
          wrappedDeliveryInputChange("delivery_address_obj", {
            address_line: "",
            province: "",
            ward: "",
            free_address: "",
          });
          isAutoFilling.current = false;
        }

        autoFillExecuted.current = true;
        lastCustomerId.current = formData.customer_id;
        shouldForceCustomerAddressUpdate.current = false;
      }
    };

    handleCustomerAddress();
  }, [
    customers,
    formData.customer_id,
    formData.delivery_address_obj,
    isEditMode,
    provinces,
    resolveProvinceId,
    wardsForCurrentProvince,
    wrappedDeliveryInputChange,
  ]);

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      
      {/* Main Content - Two Column Layout */}
      <Box sx={{ gap: 2, width: "100%", minWidth: 0 }}>
        {/* Top */}
          <TopOrder
            formData={formData}
            customers={customers}
            salesChannels={salesChannels}
            wallets={wallets}
            companies={companies}
            employees={employees}
            orderStatuses={orderStatuses}
            contextCompanyId={contextCompanyId}
            isEditMode={isEditMode}
            disabled={disabled}
            onInputChange={onInputChange}
            onOrderStatusCreated={onOrderStatusCreated}
            onOrderStatusUpdated={onOrderStatusUpdated}
            onSalesChannelCreated={onSalesChannelCreated}
            onWalletCreated={onWalletCreated}
          />

          <DeliveryInformationSection
            deliveryPhone={formData.delivery_phone}
            initialAddress={initialAddress}
            disabled={disabled}
            onDeliveryPhoneChange={(value) => onInputChange("delivery_phone", value)}
            onAddressChange={(addressValue) => {
              wrappedDeliveryInputChange("delivery_address_obj", addressValue);
            }}
          />

        {/* End top */}
        {/*  Order Items */}
        <OrderItemsTable
          orderId={orderId}
          orderItems={orderItems}
          units={units}
          productTypes={productTypes}
          disabled={disabled}
          onItemChange={onItemChange}
          onAddOrderItem={onAddOrderItem}
          onAddGroupItem={onAddGroupItem}
          onRemoveOrderItem={onRemoveOrderItem}
          onReorderItems={onReorderItems}
        />
        
        {/* Order Summary */}
        <OrderSummary
          formData={formData}
          totalAmount={totalAmount}
          additionalFees={additionalFees}
          grandTotal={grandTotal}
          orderId={orderId}
          contextCompanyId={contextCompanyId}
          disabled={disabled}
          onInputChange={onInputChange}
          onAdditionalFeesChange={onAdditionalFeesChange}
          onPaymentCollected={onPaymentCollected}
        />
      </Box>
      
      {/* Staff Information */}
      <OrderStaff
        isEditMode={isEditMode}
        formData={{
          sales_employee_id: formData.sales_employee_id,
          delivery_date: formData.delivery_date,
          survey_id: formData.survey_id,
          survey_notes: formData.survey_notes,
          survey_appointment_date: formData.survey_appointment_date,
          survey_appointment_date_text: formData.survey_appointment_date_text,
          survey_date: formData.survey_date,
          survey_date_text: formData.survey_date_text,
          tech_cnc_id: formData.tech_cnc_id,
          export_file_date: formData.export_file_date,
          export_file_date_text: formData.export_file_date_text,
        }}
        employees={employees}
        disabled={disabled}
        onInputChange={onInputChange}
      />
    </Box>
  );
};

export default OrderDetailInfo;
